import { Button, Icon, Link, Modal, ModalActions, ModalContent, ModalWrapper } from '@braskai/ui-kit';
import { useLocation, useNavigate } from 'react-router-dom';

import type { TrialPlan } from '@/features/pricing/trialPaywall/model/PLANS';

import { Stack, Typography } from '@mui/material';

import { formatBoosterPrice, formatBoosterSeatsText } from '@/features/boosters/lib/formatBoosterPrice';
import { useBoosterConfirmation } from '@/features/boosters/model/useBoosterConfirmation';
import { useBoosters } from '@/features/boosters/model/useBoosters';
import { useBoosterSeatsCounter } from '@/features/boosters/model/useBoosterSeatsCounter';
import { BoosterSeatsCounter } from '@/features/boosters/ui/BoosterSeatsCounter/BoosterSeatsCounter';
import { useTrialPaywallModalStore } from '@/features/pricing/trialPaywall/model/useTrialPaywallModalStore';

import { useBalanceStore } from '@/entities/balance';

import { PATHS } from '@/shared/constants/routes/paths';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { useInterfaceLanguage } from '@/shared/i18n';
import { createTextParser } from '@/shared/lib/createTextParser';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { BoosterType } from '@/shared/types/Billing';
import { ModalHeaderSecondary } from '@/shared/ui/modal';

import { DATA_TEST_IDS } from '../../lib/constants';
import { TRIAL_PAYWALL_TITLES } from '../../lib/limitedFeatures/paywallTitles';
import { BUTTON_TEXTS } from '../../lib/limitedFeatures/texts';
import { translateLimitedFeatureCopy } from '../../lib/limitedFeatures/translateLimitedFeatureCopy';
import { LimitedFeature } from '../../lib/types';
import { useTrialPricingModalStore } from '../../model/useTrialPricingModalStore';
import { useUpgradePlanModalContent } from '../../model/useUpgradePlanModalContent';
import { useUpgradePlanModalStore } from '../../model/useUpgradePlanModalStore';
import { SDescriptionText, SImage, SNativeVideoPlayer } from './styled';

type UpgradePlanModalProps = {
  isSpaceOwner: boolean;
  onContactSales: (sourceText: string) => void;
};

const UpgradePlanModalComponent = ({ isSpaceOwner, onContactSales }: UpgradePlanModalProps) => {
  const limitedFeature = useUpgradePlanModalStore((state) => state.limitedFeature);
  const plan = useUpgradePlanModalStore((state) => state.plan);
  const fromTrialPreview = useUpgradePlanModalStore((state) => state.fromTrialPreview);
  const close = useUpgradePlanModalStore((state) => state.close);

  const navigate = useNavigate();
  const location = useLocation();

  const { content } = useUpgradePlanModalContent(limitedFeature as LimitedFeature, plan);
  const openTrialPaywall = useTrialPaywallModalStore((state) => state.open);
  const openTrialPricingModal = useTrialPricingModalStore((state) => state.open);
  const { allBoosters, canPurchaseBoosters, purchasedBoosters } = useBoosters();
  const { locale, t } = useInterfaceLanguage();

  const { decrement, increment, seatsCount } = useBoosterSeatsCounter(1);
  const { openBoosterConfirmation } = useBoosterConfirmation();

  const { hasTrialPlan } = useBalanceStore();

  const { checkIsSpaceOwner = true } = content ?? {};

  useExecuteOneTime(() => {
    content?.mixpanelOpeningEvent();
  });

  if (!content) {
    console.error('Should show upgrade modal but no content for this plan category was provided');

    return null;
  }

  const {
    title,
    bulletsHeader,
    buttonText,
    caption,
    features,
    media: { poster, src, type: mediaType, videoOptions },
    mixpanelButtonClickEvent,
    subtitle,
  } = content;

  // Redesigned banners reserve a uniform media box so the modal height stays stable across features.
  const isRedesignedBanner = Boolean(content.content);
  let videoSx;
  if (!isRedesignedBanner) {
    videoSx = { margin: '0 auto', maxWidth: hasTrialPlan ? '356px' : '100%' };
  }

  const handleButtonClick = () => {
    mixpanelButtonClickEvent();

    if (buttonText === BUTTON_TEXTS.contactUs) {
      onContactSales(buttonText);
      return;
    }

    const selectedPlan = content?.content?.selectedPlan;
    const allowedPlanNames = content?.content?.allowedPlanNames as TrialPlan['name'][] | undefined;
    // A paywall config (even an empty Enterprise-only plan list) routes to the
    // plan-selector paywall with the feature-specific title (Figma 3-Pricing).
    const hasPaywallConfig = allowedPlanNames !== undefined || selectedPlan !== undefined;

    if (hasPaywallConfig) {
      const paywallTitle = TRIAL_PAYWALL_TITLES[limitedFeature as LimitedFeature] ?? content.title;
      openTrialPaywall({
        title: translateLimitedFeatureCopy(t, paywallTitle),
        allowedPlanNames: allowedPlanNames ?? null,
        preSelectedPlanName: (selectedPlan as TrialPlan['name'] | undefined) ?? null,
      });
    } else if (fromTrialPreview) {
      openTrialPricingModal();
    } else {
      navigate(`/${PATHS.pricing}`, { state: { path: location.pathname } });
    }

    close();
  };

  const handleBuyBoosterClick = () => {
    if (!content?.booster) return;

    MixpanelEvents.boostersAddClick({ source: 'upgrade_banner' });
    close();

    const boosterType = content.booster as BoosterType;
    const existingSeats = purchasedBoosters?.[BoosterType.TeamspaceSeat]?.quantity ?? 0;
    const isAddingSeat = boosterType === BoosterType.TeamspaceSeat;
    const finalSeatsCount = isAddingSeat ? existingSeats + seatsCount : existingSeats;

    openBoosterConfirmation({
      seatsCount: finalSeatsCount,
      selectedBoosters: {
        [BoosterType.SRT]: (purchasedBoosters?.[BoosterType.SRT]?.quantity ?? 0) > 0 || boosterType === BoosterType.SRT,
        [BoosterType.TeamspaceSeat]: finalSeatsCount > 0,
        [BoosterType.TranslationPrompting]:
          (purchasedBoosters?.[BoosterType.TranslationPrompting]?.quantity ?? 0) > 0 ||
          boosterType === BoosterType.TranslationPrompting,
      },
    });
  };

  const parseLinksAndBoldAndGrayText = createTextParser(Link, SDescriptionText);
  const translatedCaption = caption ? translateLimitedFeatureCopy(t, caption) : undefined;

  return (
    <Modal size="xs" open onClose={close}>
      <ModalHeaderSecondary
        title={translateLimitedFeatureCopy(t, title)}
        subtitle={translateLimitedFeatureCopy(t, subtitle)}
        onClose={close}
      />

      <ModalContent noPadding>
        {mediaType === 'video' ? (
          <SNativeVideoPlayer $isCover={isRedesignedBanner} poster={poster} src={src} sx={videoSx} {...videoOptions} />
        ) : (
          <SImage $isCover={isRedesignedBanner} $isGif={mediaType === 'gif'} src={src} />
        )}

        {(features?.length ?? 0) > 0 && (
          <>
            {translatedCaption && (
              <Typography color="grey.700" marginTop={8} variant="caption-1-md">
                {parseLinksAndBoldAndGrayText(translatedCaption)}
              </Typography>
            )}

            <Typography color="grey.700" component="h3" marginTop={16} variant="body-1-md">
              {translateLimitedFeatureCopy(t, bulletsHeader)}
            </Typography>

            <Stack gap={12} marginBottom={4} marginTop={12}>
              {features.map(({ description, icon }) => (
                <Stack key={description} direction="row" gap={8}>
                  <Icon color="primary.600" component={icon} size={20} />
                  <Typography color="grey.700" variant="body-2-md">
                    {parseLinksAndBoldAndGrayText(translateLimitedFeatureCopy(t, description))}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
        {content?.content && (
          <Stack marginTop={20}>
            <Typography color="grey.900" variant="body-2-md">
              {translateLimitedFeatureCopy(t, content.content.whatYouCanDo.title)}
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {content.content.whatYouCanDo.bullets.map((bullet) => (
                <li key={bullet}>
                  <Typography color="grey.700" variant="body-2">
                    {translateLimitedFeatureCopy(t, bullet)}
                  </Typography>
                </li>
              ))}
            </ul>
            {content.content.learnMore && (
              <Typography color="grey.700" marginTop={12} variant="body-2">
                <Link href={content.content.learnMore.link} rel="noreferrer noopener" size="md" target="_blank">
                  {translateLimitedFeatureCopy(t, content.content.learnMore.title)}
                </Link>{' '}
                <span style={{ fontWeight: 500 }}>
                  {translateLimitedFeatureCopy(t, content.content.learnMore.text)}
                </span>{' '}
              </Typography>
            )}
            {content.content.perfectFor && (
              <Stack
                sx={{
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 12,
                  marginTop: 12,
                  padding: 16,
                }}
              >
                <Typography color="grey.700" variant="body-2">
                  <Typography color="grey.900" component="span" variant="body-2-md">
                    {translateLimitedFeatureCopy(t, content.content.perfectFor.title)}
                  </Typography>{' '}
                  {translateLimitedFeatureCopy(t, content.content.perfectFor.text)}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </ModalContent>

      <ModalActions>
        <Stack direction="column" gap={12} width="100%">
          {!IS_WHITE_LABEL && content?.booster && canPurchaseBoosters && (
            <Stack flexDirection="row" gap={8}>
              {content?.booster === BoosterType.TeamspaceSeat && (
                <BoosterSeatsCounter
                  seatsCount={seatsCount}
                  variant="bordered"
                  onDecrement={decrement}
                  onIncrement={increment}
                />
              )}
              <Button
                data-testid="upgrade-plan-modal-buy-booster-click"
                size="lg"
                variant="outlined"
                fullWidth
                onClick={handleBuyBoosterClick}
              >
                {content.booster === BoosterType.TeamspaceSeat
                  ? formatBoosterSeatsText(seatsCount, allBoosters, locale, t)
                  : t('pricing.booster.addForMonth', {
                      price: formatBoosterPrice(allBoosters, content.booster, seatsCount, locale),
                    })}
              </Button>
            </Stack>
          )}

          {!IS_WHITE_LABEL && (
            <Button
              data-testid={DATA_TEST_IDS.upgradeButton}
              disabled={checkIsSpaceOwner && !isSpaceOwner}
              size="lg"
              variant="primary"
              fullWidth
              onClick={handleButtonClick}
            >
              {translateLimitedFeatureCopy(t, buttonText)}
            </Button>
          )}
        </Stack>
      </ModalActions>
    </Modal>
  );
};

export const UpgradePlanModal = ({ isSpaceOwner, onContactSales }: UpgradePlanModalProps) => (
  <ModalWrapper hook={useUpgradePlanModalStore}>
    <UpgradePlanModalComponent isSpaceOwner={isSpaceOwner} onContactSales={onContactSales} />
  </ModalWrapper>
);
